import { Switch, Route, useLocation } from 'wouter';
import { useEffect, Suspense, lazy } from 'react';
import { queryClient } from './lib/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/toaster';
import { TooltipProvider } from './components/ui/tooltip';
import { useAnalytics } from './lib/analytics';
import { Concierge } from '@/components/concierge/Concierge';
import { ConsentBanner } from './components/consent/ConsentBanner';
import './styles/world.css';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AdminRoute } from './components/auth/AdminRoute';
import { useEnsureConvexUser } from './hooks/useEnsureConvexUser';
import { usePostHogIdentify } from './hooks/usePostHogIdentify';

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-border"></div>
  </div>
);

// Lazy load all pages for code splitting
const Home = lazy(() => import('./pages/home'));
const Products = lazy(() => import('./pages/products'));
const DeckBoxes = lazy(() => import('./pages/deck-boxes'));
const Binders = lazy(() => import('./pages/binders'));
const CardProtection = lazy(() => import('./pages/card-protection'));
const BlogIndex = lazy(() => import('./pages/blog/index'));
const BlogArticle = lazy(() => import('./pages/blog/article'));
const UnsubscribePage = lazy(() => import('./pages/unsubscribe'));
const TabletopAccessories = lazy(() => import('./pages/tabletop-accessories'));
const CollectorProfile = lazy(() => import('./pages/collector-profile'));
const ProductDetail = lazy(() => import('./pages/product-detail'));
const Binder216 = lazy(() => import('./pages/binder-216'));
const About = lazy(() => import('./pages/about'));
const FAQ = lazy(() => import('./pages/faq'));
const Shipping = lazy(() => import('./pages/shipping'));
const Returns = lazy(() => import('./pages/returns'));
const Contact = lazy(() => import('./pages/contact'));
const PrivacyPolicy = lazy(() => import('./pages/privacy-policy'));
const VIPDashboardDemo = lazy(() => import('./pages/vip-dashboard-demo'));
const TestPurchase = lazy(() => import('./pages/test-purchase'));
const Vault = lazy(() => import('./pages/vault'));
const ScratchWin = lazy(() => import('./pages/scratch-win'));
const ScratchWinIframe = lazy(() => import('./pages/scratch-win-iframe'));
const ConversationReward = lazy(() => import('./pages/conversation-reward'));
const InsertPromo = lazy(() => import('./pages/insert-promo'));
const InsertReward = lazy(() => import('./pages/insert-reward'));
const LoginPage = lazy(() => import('./pages/login'));
const RewardsJourney = lazy(() => import('./pages/rewards-journey'));
const RewardsPage = lazy(() => import('./pages/rewards'));
const CollectorJourney = lazy(() => import('./pages/collector-journey'));
const ChooseAdventure = lazy(() => import('./pages/choose-adventure'));
const Adventure = lazy(() => import('./pages/adventure'));
const WorldSports = lazy(() => import('./pages/world-sports'));
const WorldFantasy = lazy(() => import('./pages/world-fantasy'));
const ProductQuizPage = lazy(() => import('./pages/product-quiz'));
const QuizEmbedPage = lazy(() => import('./pages/quiz-embed'));
const SignInPage = lazy(() => import('./pages/sign-in-clerk'));
const CreateAccountPage = lazy(() => import('./pages/create-account-clerk'));
const ProfileCompletionPage = lazy(() => import('./pages/profile-completion'));
const WelcomePage = lazy(() => import('./pages/welcome'));
const ScratchWinPromoLinksPage = lazy(() => import('./pages/scratch-win-promo-links'));
const OrderPendingApprovalPage = lazy(() => import('./pages/order-pending-approval'));
const AuthErrorPage = lazy(() => import('./pages/auth-error'));
const AdminClaims = lazy(() => import('./pages/admin/claims'));
const AdminTickets = lazy(() => import('./pages/admin/tickets'));
const AdminConcierge = lazy(() => import('./pages/admin/concierge'));
const AdminDashboard = lazy(() => import('./pages/admin/dashboard'));
const AdminCommunications = lazy(() => import('./pages/admin/communications'));
const AdminBlogReview = lazy(() => import('./pages/admin/blog-review'));
const AdminFulfillment = lazy(() => import('./pages/admin/fulfillment'));
const AdminD2cDashboard = lazy(() => import('./pages/admin/dashboard-d2c'));
const AdminFunnelTime = lazy(() => import('./pages/admin/funnel-time'));
const AdminFunnelDashboard = lazy(() => import('./pages/admin/dashboard-funnel'));
const CheckoutPage = lazy(() => import('./pages/checkout'));
const OrderConfirmationPage = lazy(() => import('./pages/order-confirmation'));
const NotFound = lazy(() => import('./pages/not-found'));

function ScrollToTop() {
  const [location] = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return null;
}

function Router() {
  // Track page views automatically
  useAnalytics();

  // Auto-create user in Convex after Clerk sign-in (local dev workaround)
  useEnsureConvexUser();

  // Stitch the anonymous pre-sign-in session (e.g. an unauthenticated wheel
  // spin) to the Clerk user once they authenticate.
  usePostHogIdentify();

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/auth-error" component={AuthErrorPage} />
          <Route path="/deck-boxes" component={DeckBoxes} />
          <Route path="/binders" component={Binders} />
          <Route path="/card-protection" component={CardProtection} />
          <Route path="/blog" component={BlogIndex} />
          <Route path="/blog/:slug" component={BlogArticle} />
          <Route path="/tabletop-accessories" component={TabletopAccessories} />
          <Route path="/collector-profile">
            <ProtectedRoute>
              <CollectorProfile />
            </ProtectedRoute>
          </Route>
          <Route path="/products" component={Products} />
          {/* Must precede /products/:id — wouter matches in order, so the
              param route would otherwise swallow "binder-216" as an id. */}
          <Route path="/products/binder-216" component={Binder216} />
          <Route path="/products/:id" component={ProductDetail} />
          <Route path="/about" component={About} />
          <Route path="/faq" component={FAQ} />
          <Route path="/shipping" component={Shipping} />
          <Route path="/returns" component={Returns} />
          <Route path="/contact" component={Contact} />
          <Route path="/privacy-policy" component={PrivacyPolicy} />
          <Route path="/vip-dashboard-demo" component={VIPDashboardDemo} />
          <Route path="/test-purchase" component={TestPurchase} />
          <Route path="/promo/insert/reward/:token" component={InsertReward} />
          <Route path="/promo/insert/:campaignKey" component={InsertPromo} />
          <Route path="/promo/scratch-win/:campaignKey" component={ScratchWin} />
          {/* /vault is The Combination — the page the homepage hero CTA
              ("Find your combination" / "Open your vault") promises. It was
              aliased to Scratch & Win from 2026-08-23 (fa068e7f) while
              incentiveCodePool was empty; restored 2026-09-02 because a new
              visitor following the hero landed on a prize wheel instead of the
              game they were offered. The page degrades honestly on an empty
              pool (reason:"pool_empty" copy), and codes are issued once a
              Seller Central batch is loaded via collector:loadCodeBatch. */}
          <Route path="/vault" component={Vault} />
          <Route path="/scratch-win" component={ScratchWin} />
          <Route path="/scratch-win-iframe" component={ScratchWinIframe} />
          <Route path="/promo" component={ScratchWin} />
          <Route path="/login/:claimId?" component={LoginPage} />
          <Route path="/conversation/reward/:token" component={ConversationReward} />
          <Route path="/rewards-journey" component={RewardsJourney} />
          <Route path="/rewards" component={RewardsPage} />
          <Route path="/collector-journey" component={CollectorJourney} />
          <Route path="/choose-adventure" component={ChooseAdventure} />
          <Route path="/adventure" component={Adventure} />
          <Route path="/world-sports" component={WorldSports} />
          <Route path="/world-fantasy" component={WorldFantasy} />
          <Route path="/product-quiz" component={ProductQuizPage} />
          <Route path="/quiz-embed" component={QuizEmbedPage} />
          <Route path="/sign-in" component={SignInPage} />
          <Route path="/create-account" component={CreateAccountPage} />
          <Route path="/complete-profile" component={ProfileCompletionPage} />
          <Route path="/profile-completion" component={ProfileCompletionPage} />
          {/* Token unsubscribe from factory emails — public on purpose: the
              audience includes recipients with no account, and an opt-out
              behind a sign-in wall is a broken opt-out. */}
          <Route path="/unsubscribe" component={UnsubscribePage} />
          {/* Post-signup consent step and the account-holder preferences
              page. Both require a signed-in user. */}
          <Route path="/welcome">
            <ProtectedRoute>
              <WelcomePage />
            </ProtectedRoute>
          </Route>
          <Route path="/email-preferences">
            <ProtectedRoute>
              <WelcomePage />
            </ProtectedRoute>
          </Route>
          <Route path="/admin/claims">
            <AdminRoute>
              <AdminClaims />
            </AdminRoute>
          </Route>
          <Route path="/admin/tickets">
            <AdminRoute>
              <AdminTickets />
            </AdminRoute>
          </Route>
          <Route path="/admin/concierge">
            <AdminRoute>
              <AdminConcierge />
            </AdminRoute>
          </Route>
          <Route path="/admin/funnel-time">
            <AdminRoute>
              <AdminFunnelTime />
            </AdminRoute>
          </Route>
          <Route path="/admin/dashboard/d2c">
            <AdminRoute>
              <AdminD2cDashboard />
            </AdminRoute>
          </Route>
          <Route path="/admin/dashboard/funnel">
            <AdminRoute>
              <AdminFunnelDashboard />
            </AdminRoute>
          </Route>
          <Route path="/admin/dashboard">
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          </Route>
          <Route path="/admin/blog-review">
            <AdminRoute>
              <AdminBlogReview />
            </AdminRoute>
          </Route>
          <Route path="/admin/fulfillment">
            <AdminRoute>
              <AdminFulfillment />
            </AdminRoute>
          </Route>
          <Route path="/admin/communications">
            <AdminRoute>
              <AdminCommunications />
            </AdminRoute>
          </Route>
          <Route path="/scratch-win-promo-links" component={ScratchWinPromoLinksPage} />
          <Route path="/checkout" component={CheckoutPage} />
          {/* Stripe successUrl target. Without this a paid customer lands on a 404. */}
          <Route path="/order-confirmation" component={OrderConfirmationPage} />
          <Route path="/order-pending-approval" component={OrderPendingApprovalPage} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </>
  );
}

function App() {

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <div className="min-h-screen bg-background text-foreground">
          <Toaster />
          <Router />
          {/* Sits outside Router so the banner survives navigation — a visitor
              must not have to answer it again on every route change. */}
          <ConsentBanner />
          <Concierge />
        </div>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
